import impeccableReputationChecksService from '@/api/impeccable-reputation-checks-service';
import { updateArrayItem } from '@/util/array';
import eventBus, { OPEN_SNACKBAR } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { mapErrorsToInputs } from '@/util/forms';
import { format } from 'date-fns';

const getDefaultFormItem = () => ({});

export const initialImpeccableReputationChecksFilterParams = () => ({
  statuses: [],
});

const state = {
  impeccableReputationChecks: null,
  impeccableReputationChecksPagination: {},
  newImpeccableReputationCheck: getDefaultFormItem(),
  editedImpeccableReputationCheck: {},
  impeccableReputationCheckValidationErrors: {},
  impeccableReputationChecksFilterParams: initialImpeccableReputationChecksFilterParams(),
};

const getters = {
  reputationStatusMap() {
    return {
      not_checked: {
        label: i18n.t('reputation_statuses.not_checked'),
        value: 'not_checked',
        color: 'grey lighten-2',
      },
      impeccable: {
        label: i18n.t('reputation_statuses.impeccable'),
        value: 'impeccable',
        color: 'green accent-2',
      },
      not_impeccable: {
        label: i18n.t('reputation_statuses.not_impeccable'),
        value: 'not_impeccable',
        color: 'red accent-1',
      },
      checking_in_progress: {
        label: i18n.t('reputation_statuses.checking_in_progress'),
        value: 'checking_in_progress',
        color: 'yellow darken-1',
      },
      invalid_person_data: {
        label: i18n.t('reputation_statuses.invalid_person_data'),
        value: 'invalid_person_data',
        color: 'yellow accent-1',
      },
      person_data_fined_down: {
        label: i18n.t('reputation_statuses.person_data_fined_down'),
        value: 'person_data_fined_down',
        color: 'blue accent-1',
      },
      unable_to_check: {
        label: i18n.t('reputation_statuses.unable_to_check'),
        value: 'unable_to_check',
        color: 'red accent-1',
      },
    };
  },

  reputationStatuses(state, getters) {
    return Object.values(getters.reputationStatusMap);
  },

  institutions() {
    return [
      {
        value: 1,
        text: i18n.t('institution_1'),
      },
      {
        value: 2,
        text: i18n.t('institution_2'),
      },
      {
        value: 3,
        text: i18n.t('institution_3'),
      },
      {
        value: 4,
        text: i18n.t('institution_4'),
      },
    ];
  },
};

const mutations = {
  SET_IMPECCABLE_REPUTATION_CHECKS(state, { data, meta }) {
    state.impeccableReputationChecks = data;
    state.impeccableReputationChecksPagination = {
      current_page: meta.current_page,
      per_page: meta.per_page,
      total: meta.total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.impeccableReputationChecksFilterParams = params;
  },

  SET_EDITED_IMPECCABLE_REPUTATION_CHECK(state, impeccableReputationCheck) {
    state.impeccableReputationCheckValidationErrors = {};
    state.editedImpeccableReputationCheck = JSON.parse(JSON.stringify(impeccableReputationCheck));
  },

  UPDATE_IMPECCABLE_REPUTATION_CHECK(state, impeccableReputationCheck) {
    state.impeccableReputationChecks = updateArrayItem(
      state.impeccableReputationChecks,
      impeccableReputationCheck,
    );
  },

  UPDATE_IMPECCABLE_REPUTATION_CHECK_INSTITUTION(state, { id, institution_id }) {
    for (let i = 0; i < state.impeccableReputationChecks.length; i++) {
      if (
        state.impeccableReputationChecks[i].id === id &&
        state.impeccableReputationChecks[i][`status_${institution_id}`] === 'not_checked'
      ) {
        state.impeccableReputationChecks[i][`status_${institution_id}`] = 'checking_in_progress';
        state.impeccableReputationChecks[i][`check_date_institution${institution_id}`] = format(
          new Date(),
          'yyyy-MM-dd HH:mm',
        );
      }
    }
  },

  UPDATE_MULTIPLE_REPUTATION_CHECK_INSTITUTION(state, { id, institution_id }) {
    for (let i = 0; i < state.impeccableReputationChecks.length; i++) {
      for (let j = 0; j < id.length; j++) {
        if (
          state.impeccableReputationChecks[i].id === id[j] &&
          state.impeccableReputationChecks[i][`status_${institution_id}`] === 'not_checked'
        ) {
          state.impeccableReputationChecks[i][`status_${institution_id}`] = 'checking_in_progress';
          state.impeccableReputationChecks[i][`check_date_institution${institution_id}`] = format(
            new Date(),
            'yyyy-MM-dd HH:mm',
          );
        }
      }
    }
  },

  UPDATE_MULTIPLE_IMPECCABLE_REPUTATION_CHECKS(state, reputationChecks) {
    for (let i = 0; i < reputationChecks.length; i++) {
      state.impeccableReputationChecks = updateArrayItem(
        state.impeccableReputationChecks,
        reputationChecks[i],
      );
    }
  },

  SET_IMPECCABLE_REPUTATION_CHECK_VALIDATION_ERRORS(state, validationErrors) {
    state.impeccableReputationCheckValidationErrors = validationErrors;
  },

  RESET_IMPECCABLE_REPUTATION_CHECK_VALIDATION_ERRORS(state) {
    state.impeccableReputationCheckValidationErrors = {};
  },

  CLEAR_IMPECCABLE_REPUTATION_CHECK_VALIDATION_ERRORS(state, field) {
    delete state.impeccableReputationCheckValidationErrors[field];
  },
};

const actions = {
  fetchImpeccableReputationChecks({ commit }, params) {
    commit('SET_FILTER_PARAMS', params);
    return impeccableReputationChecksService.getPage(params).then((res) => {
      commit('SET_IMPECCABLE_REPUTATION_CHECKS', res.data);
      return res.data;
    });
  },

  editImpeccableReputationCheck({ state, commit }, impeccableReputationCheckId) {
    const impeccableReputationCheck = state.impeccableReputationChecks?.find(
      (s) => s.id === impeccableReputationCheckId,
    );
    if (impeccableReputationCheck) {
      commit('SET_EDITED_IMPECCABLE_REPUTATION_CHECK', impeccableReputationCheck);
      return Promise.resolve(impeccableReputationCheck);
    }
    return impeccableReputationChecksService.getById(impeccableReputationCheckId).then((res) => {
      commit('SET_EDITED_IMPECCABLE_REPUTATION_CHECK', res.data.data);
      return res.data.data;
    });
  },

  updateImpeccableReputationCheck({ commit }, impeccableReputationCheck) {
    return impeccableReputationChecksService
      .update(impeccableReputationCheck)
      .then((res) => {
        commit('UPDATE_IMPECCABLE_REPUTATION_CHECK', res.data.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('background_check_updated'));
      })
      .catch((err) => {
        commit('SET_IMPECCABLE_REPUTATION_CHECK_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  updateMultipleImpeccableReputationChecksStatuses({ commit }, payload) {
    return impeccableReputationChecksService.updateMultipleStatuses(payload).then((res) => {
      commit('UPDATE_MULTIPLE_IMPECCABLE_REPUTATION_CHECKS', res.data.data);
      eventBus.$emit(OPEN_SNACKBAR, i18n.t('statuses_changed'));
      return res.data.data;
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
